import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { API_URL } from '../config'
import EventContext from '../contexts/EventContext'
import Layout from './Layout'
import Loader from './Loader'
import useApiUrlTranslation from './useApiUrlTranslation'

const FIXED_WIDTH = 1280;
const FIXED_HEIGHT = 720;

const ScreenPreview = ({ title, iframeSrc, editUrl, editLabel }) => {
    const wrapperRef = useRef(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const updateScale = () => {
            const width = wrapper.offsetWidth;
            if (width > 0) setScale(width / FIXED_WIDTH);
        };
        const observer = new ResizeObserver(updateScale);
        observer.observe(wrapper);
        updateScale();
        return () => observer.disconnect();
    }, []);

    return (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl shadow-sm mb-6 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-neutral-800">
                <h3 className="font-bold text-gray-800 dark:text-white text-sm">{title}</h3>
                {editUrl && (
                    <a
                        href={editUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-e2o-600 dark:text-e2o-400 border border-e2o-200 dark:border-e2o-800 hover:bg-e2o-50 dark:hover:bg-e2o-950/30 rounded-lg transition-colors"
                    >
                        ✎ {editLabel || 'Modifier'}
                    </a>
                )}
            </div>
            <div
                ref={wrapperRef}
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    aspectRatio: `${FIXED_WIDTH} / ${FIXED_HEIGHT}`,
                }}
            >
                <iframe
                    title={title}
                    src={iframeSrc}
                    style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: `${FIXED_WIDTH}px`,
                        height: `${FIXED_HEIGHT}px`,
                        border: 'none',
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                    }}
                />
            </div>
        </div>
    );
};

const SlotCard = ({ confEvent }) => {
    if (!confEvent?.id_conf_event) return null;

    return (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl shadow-sm p-5 mb-6">
            <h3 className="font-bold text-gray-800 dark:text-white text-sm mb-3">Rappel de votre rendez-vous</h3>
            <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-e2o-100 dark:bg-e2o-900/30">
                    <span className="text-2xl">📅</span>
                </div>
                <div>
                    <div className="text-lg font-bold text-gray-800 dark:text-white">
                        {confEvent.heure_debut?.slice(0, 5)} – {confEvent.heure_fin?.slice(0, 5)}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-neutral-400">
                        {confEvent.conf_event_date}
                    </div>
                    {confEvent.conf_event_lang?.cel_titre && (
                        <div className="text-sm text-gray-600 dark:text-neutral-300 mt-1">
                            {confEvent.conf_event_lang.cel_titre}
                        </div>
                    )}
                    <div className="text-xs text-gray-400 dark:text-neutral-500 mt-1">
                        Session #{confEvent.id_conf_event}
                    </div>
                </div>
            </div>
        </div>
    );
};

const WebCastValidation = () => {
    const { idEvent, idContact } = useParams();
    const { currentEvent } = useContext(EventContext);
    const { apiUrlTranslation } = useApiUrlTranslation();

    const [contact, setContact] = useState(null);
    const [confEvent, setConfEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [validated, setValidated] = useState(false);

    const idEventLigne = currentEvent?.event_ligne?.id_event_ligne;

    useEffect(() => {
        if (!idContact) return;
        const controller = new AbortController();

        Promise.all([
            fetch(`${API_URL}?action=getContact&id_contact=${idContact}`, { signal: controller.signal }).then(r => r.json()),
            fetch(`${apiUrlTranslation}?action=getPartenaires&params=AND cf.id_event=${idEvent} AND c.id_contact=${idContact} AND cf.id_conf_event NOT IN("",0) LIMIT 1`, { signal: controller.signal }).then(r => r.json()),
        ])
            .then(([contactData, partenaireData]) => {
                setContact(contactData);
                if (Array.isArray(partenaireData) && partenaireData[0]?.conf_event) {
                    setConfEvent(partenaireData[0].conf_event);
                }
                setLoading(false);
            })
            .catch(err => { if (err.name !== 'AbortError') { console.error(err); setLoading(false); } });

        return () => controller.abort();
    }, [idContact, idEvent, apiUrlTranslation]);

    useEffect(() => {
        const handleCcvMessage = (e) => {
            if (!e.data || e.data.ccv !== 'dataUpdated') return;
            document.querySelectorAll('iframe').forEach((f) => {
                try {
                    f.contentWindow.location.reload();
                } catch (err) {
                    try { f.src = f.src; } catch (err2) { /* ignore */ }
                }
            });
        };
        window.addEventListener('message', handleCcvMessage);
        return () => window.removeEventListener('message', handleCcvMessage);
    }, []);

    if (loading || !currentEvent) {
        return <Layout><div className="flex items-center justify-center h-screen"><Loader /></div></Layout>;
    }

    if (!contact) {
        return <Layout><div className="p-8 text-center text-gray-500">Contact introuvable.</div></Layout>;
    }
    //https://www.event2one.com/screen_manager/content/contact/index.dev.php?id_cycle_lang=&id_contact=227438&content=background_image&target=controle&id_event_ligne=663&fiche_production_mode=game_changer&id_formulaire_list=&id_scene_virale=&visible_section_id_list=camera_section,demo_presentation_section,logo_section,titrage_session_2
    const baseIframeUrl = `https://www.event2one.com/screen_manager/content/contact/index.dev.php?id_cycle_lang=&id_contact=${idContact}&content=background_image&target=controle&id_event_ligne=${idEventLigne}&edit=0&fiche_production_mode=game_changer`;

    const signupUrl = `/events/${idEvent}/signup/enregistrement-webcast/c/${idContact}?form-name=enregistrement-webcast&formisvisible=1&id_formulaire=105`;

    const sections = [
        {
            title: "Écran d'introduction",
            // iframeSrc: `${baseIframeUrl}&visible_section_id_list=titrage_session_haut_droit,titrage_session,custom_section,demo_presentation_right_section,contact_logo`,
            iframeSrc: `${baseIframeUrl}&visible_section_id_list=video_presentation_fullscreen_section,logo_event_ligne_section,titrage_bas_large`,
            editUrl: signupUrl,
            editLabel: 'Modifier le titrage',
        },
        {
            title: "Écran titre et logo",
            iframeSrc: `${baseIframeUrl}&visible_section_id_list=titrage_section,logo_section,video_stream_fullscreen_section`,
            editUrl: signupUrl,
            editLabel: 'Modifier le contact',
        },
        {//
            title: "Écran vidéo incrustée",
            iframeSrc: `${baseIframeUrl}&visible_section_id_list=camera_section,demo_presentation_section,logo_event_ligne_section`,
            editUrl: signupUrl,
            editLabel: 'Modifier la vidéo',
        },
        {
            title: "Écran vidéo plein écran",
            iframeSrc: `${baseIframeUrl}&visible_section_id_list=video_presentation_fullscreen_section,logo_event_ligne_section,titrage_bas_large`,
            editUrl: signupUrl,
            editLabel: 'Modifier la vidéo',
        },
    ];

    return (
        <Layout>
            <div className="bg-gray-50 dark:bg-neutral-950 min-h-screen p-4 md:p-8">

                <div className="max-w-4xl mx-auto">

                    <header className="mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                            Formulaire de Validation de votre Webcast
                        </h1>
                        <p className="text-gray-500 dark:text-neutral-400 mt-1">
                            {contact.prenom} {contact.nom} — {contact.societe}
                        </p>
                    </header>

                    <div className="mb-6 p-4 bg-sky-50 dark:bg-sky-950/20 border-l-4 border-sky-500 rounded-lg text-sm text-sky-900 dark:text-sky-200">
                        <p className="font-bold mb-1">Comment valider votre webcast ?</p>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Vérifiez chaque aperçu ci-dessous — ils montrent exactement ce qui sera diffusé à l'antenne.</li>
                            <li>Cliquez sur <strong>"Modifier"</strong> pour corriger un écran si nécessaire.</li>
                            <li>Une fois satisfait(e), cliquez sur <strong>"Je valide le contenu de mon webcast"</strong> en bas de page.</li>
                        </ol>
                    </div>

                    <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">
                        Validation de vos titrages et écrans
                    </h2>

                    {sections.map((section, index) => (
                        <ScreenPreview
                            key={index}
                            title={section.title}
                            iframeSrc={section.iframeSrc}
                            editUrl={section.editUrl}
                            editLabel={section.editLabel}
                        />
                    ))}

                    <SlotCard confEvent={confEvent} />

                    <div className="mt-8 mb-12">
                        {validated ? (
                            <div className="p-6 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-xl text-center">
                                <span className="text-4xl">✅</span>
                                <p className="text-lg font-bold text-green-700 dark:text-green-400 mt-2">
                                    Votre webcast a été validé avec succès !
                                </p>
                                <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                                    L'équipe technique a été notifiée.
                                </p>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setValidated(true)}
                                className="w-full px-6 py-5 bg-e2o-500 hover:bg-e2o-600 active:bg-e2o-700 text-white font-bold rounded-xl shadow-lg transition-colors text-lg"
                            >
                                <div className="flex flex-col items-center gap-1">
                                    <span>Je valide le contenu de mon webcast</span>
                                    <span className="text-sm font-normal opacity-80">et confirme que les aperçus sont corrects</span>
                                </div>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default WebCastValidation
